import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';

/*
  Reusable SEO component
  Props:
  - title: string
  - description: string (150-160 chars ideal)
  - canonical: absolute URL; if omitted, computed from window.location.href in browser
  - image: absolute URL for OG/Twitter
  - noIndex: boolean (adds robots noindex,nofollow)
  - jsonLd: object | array of objects for structured data
*/
export default function SEO({ title, description, canonical, image, noIndex = false, jsonLd }) {
    const computedCanonical = canonical || (typeof window !== 'undefined' ? window.location.href : undefined);

    const meta = [
        description ? { name: 'description', content: description } : null,
        { property: 'og:type', content: 'website' },
        title ? { property: 'og:title', content: title } : null,
        description ? { property: 'og:description', content: description } : null,
        image ? { property: 'og:image', content: image } : null,
        title ? { name: 'twitter:title', content: title } : null,
        description ? { name: 'twitter:description', content: description } : null,
        { name: 'twitter:card', content: image ? 'summary_large_image' : 'summary' },
        image ? { name: 'twitter:image', content: image } : null,
        noIndex ? { name: 'robots', content: 'noindex,nofollow' } : { name: 'robots', content: 'index,follow' },
    ].filter(Boolean);

    return (
        <Helmet prioritizeSeoTags>
            {title && <title>{title}</title>}
            {computedCanonical && <link rel="canonical" href={computedCanonical} />}
            {meta.map((m, i) => (
                <meta key={i} {...m} />
            ))}
            {jsonLd &&
                (Array.isArray(jsonLd) ? (
                    jsonLd.map((data, idx) => (
                        <script key={idx} type="application/ld+json">
                            {JSON.stringify(data)}
                        </script>
                    ))
                ) : (
                    <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
                ))}
        </Helmet>
    );
}

SEO.propTypes = {
    title: PropTypes.string,
    description: PropTypes.string,
    canonical: PropTypes.string,
    image: PropTypes.string,
    noIndex: PropTypes.bool,
    jsonLd: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
};
